/**
 * @module citationPlugin
 * @description Custom Milkdown plugin for rendering Pandoc citations
 *
 * In WYSIWYG mode, citations like [@key] or [@key, p. 42] are rendered as
 * styled badges showing the formatted citation (using citeproc-js + CSL).
 * In source/markdown mode, the raw [@key] text is preserved.
 *
 * Uses ProseMirror decorations (Decoration.inline + Decoration.widget).
 * Delegates citation formatting to citationService.ts.
 */

import { Plugin, PluginKey } from "@milkdown/kit/prose/state";
import { Decoration, DecorationSet } from "@milkdown/kit/prose/view";
import { $prose } from "@milkdown/kit/utils";
import { citationService } from "../services/citationService";
import { items as zoteroItems, loadItemsByKeys, isInitialized as zoteroInitialized, selectedLibrary } from "../stores/zoteroStore";
import { templates, projectDocsOrdered, selectedDoc } from "../stores/appStore";
import { currentDocId } from "../stores/editorStore";
import { db } from "../services/indexedDBService";
import { get } from "svelte/store";
import type { Template, Document } from "../types/models";
import { initZotero } from "../stores/zoteroStore";

// ============================================================================
// TYPES
// ============================================================================

/** Parsed citation from markdown */
interface ParsedCitation {
  key: string;
  locator?: string;
  label?: string;
  prefix?: string;
  suffix?: string;
  raw: string;
}

/** Field info for editable citations */
interface EditableField {
  field: string;
  value: string;
  displayValue: string; // May include HTML like <i>
}

// ============================================================================
// FIELD-AWARE RENDERING
// ============================================================================

/**
 * CSL field to Zotero field mapping (for writing back to Zotero)
 */
const CSL_TO_ZOTERO_FIELD: Record<string, string> = {
  'title': 'title',
  'container-title': 'publicationTitle',
  'publisher': 'publisher',
  'publisher-place': 'place',
  'issued': 'date',
  'original-date': 'extra', // Stored in extra as "original-date: YYYY"
  'volume': 'volume',
  'issue': 'issue',
  'page': 'pages',
  'edition': 'edition',
  'collection-title': 'series',
  'ISBN': 'ISBN',
  'ISSN': 'ISSN',
  'DOI': 'DOI',
  'URL': 'url',
};

/**
 * Extract fields from a CSL item for display
 */
function extractEditableFields(cslItem: any): EditableField[] {
  const fields: EditableField[] = [];

  // Author(s)
  if (cslItem.author && cslItem.author.length > 0) {
    const authorStr = cslItem.author.map((a: any) => {
      if (a.literal) return a.literal;
      const parts = [];
      if (a.given) parts.push(a.given.charAt(0) + '.');
      if (a.family) parts.push(a.family);
      return parts.join(' ');
    }).join(', ');
    fields.push({ field: 'author', value: authorStr, displayValue: authorStr });
  }

  // Title (with italics for books/articles)
  if (cslItem.title) {
    const isItalic = ['book', 'thesis', 'report', 'motion_picture'].includes(cslItem.type);
    fields.push({
      field: 'title',
      value: cslItem.title,
      displayValue: isItalic ? `<i>${cslItem.title}</i>` : cslItem.title
    });
  }

  // Original date (for reprints)
  if (cslItem['original-date']) {
    const origDate = formatCSLDate(cslItem['original-date']);
    if (origDate) {
      fields.push({ field: 'original-date', value: origDate, displayValue: origDate });
    }
  }

  // Container title (journal, book title)
  if (cslItem['container-title']) {
    fields.push({
      field: 'container-title',
      value: cslItem['container-title'],
      displayValue: `<i>${cslItem['container-title']}</i>`
    });
  }

  // Volume and issue
  if (cslItem.volume) {
    let volStr = cslItem.volume;
    if (cslItem.issue) {
      volStr += `(${cslItem.issue})`;
    }
    fields.push({ field: 'volume', value: cslItem.volume, displayValue: volStr });
  }

  // Publisher place
  if (cslItem['publisher-place']) {
    fields.push({
      field: 'publisher-place',
      value: cslItem['publisher-place'],
      displayValue: cslItem['publisher-place']
    });
  }

  // Publisher
  if (cslItem.publisher) {
    fields.push({
      field: 'publisher',
      value: cslItem.publisher,
      displayValue: cslItem.publisher
    });
  }

  // Issued date
  if (cslItem.issued) {
    const dateStr = formatCSLDate(cslItem.issued);
    if (dateStr) {
      fields.push({ field: 'issued', value: dateStr, displayValue: dateStr });
    }
  }

  // Pages
  if (cslItem.page) {
    fields.push({ field: 'page', value: cslItem.page, displayValue: `p. ${cslItem.page}` });
  }

  return fields;
}

/**
 * Format a CSL date to string
 */
function formatCSLDate(date: any): string | null {
  if (!date) return null;
  if (date.raw) return date.raw;
  if (date.literal) return date.literal;
  if (date['date-parts'] && date['date-parts'][0]) {
    const parts = date['date-parts'][0];
    if (parts.length >= 1) {
      return parts[0].toString();
    }
  }
  return null;
}

/**
 * Generate field-aware HTML for a citation
 * Each field is wrapped in a span with data-field attribute for editing
 */
function generateEditableHTML(cslItem: any): string {
  const fields = extractEditableFields(cslItem);

  if (fields.length === 0) {
    return `<span class="csl-field" data-field="title">${cslItem.title || 'Untitled'}</span>`;
  }

  const parts: string[] = [];

  // Build formatted citation string based on style (simplified note style)
  const authorField = fields.find(f => f.field === 'author');
  const titleField = fields.find(f => f.field === 'title');
  const origDateField = fields.find(f => f.field === 'original-date');
  const containerField = fields.find(f => f.field === 'container-title');
  const placeField = fields.find(f => f.field === 'publisher-place');
  const publisherField = fields.find(f => f.field === 'publisher');
  const issuedField = fields.find(f => f.field === 'issued');
  const volumeField = fields.find(f => f.field === 'volume');
  const pageField = fields.find(f => f.field === 'page');

  // Author
  if (authorField) {
    parts.push(`<span class="csl-field" data-field="author">${authorField.displayValue}</span>`);
  }

  // Title with optional original date
  if (titleField) {
    let titlePart = `<span class="csl-field" data-field="title">${titleField.displayValue}</span>`;
    if (origDateField) {
      titlePart += ` (<span class="csl-field" data-field="original-date">${origDateField.displayValue}</span>)`;
    }
    parts.push(titlePart);
  }

  // Container (journal/book)
  if (containerField) {
    let containerPart = `<span class="csl-field" data-field="container-title">${containerField.displayValue}</span>`;
    if (volumeField) {
      containerPart += `, <span class="csl-field" data-field="volume">${volumeField.displayValue}</span>`;
    }
    parts.push(containerPart);
  }

  // Place, Publisher, Date
  const pubParts: string[] = [];
  if (placeField) {
    pubParts.push(`<span class="csl-field" data-field="publisher-place">${placeField.displayValue}</span>`);
  }
  if (publisherField) {
    pubParts.push(`<span class="csl-field" data-field="publisher">${publisherField.displayValue}</span>`);
  }
  if (issuedField) {
    pubParts.push(`<span class="csl-field" data-field="issued">${issuedField.displayValue}</span>`);
  }
  if (pubParts.length > 0) {
    parts.push(pubParts.join(', '));
  }

  // Pages
  if (pageField) {
    parts.push(`<span class="csl-field" data-field="page">${pageField.displayValue}</span>`);
  }

  return parts.join(', ') + '.';
}

/**
 * Generate field-aware HTML for a bibliography entry
 * Uses bibliography format: "Surname, First Name. Title. Place: Publisher, Year."
 */
function generateBibliographyEditableHTML(cslItem: any): string {
  const fields = extractEditableFields(cslItem);

  if (fields.length === 0) {
    return `<span class="csl-field" data-field="title">${cslItem.title || 'Untitled'}</span>`;
  }

  const parts: string[] = [];

  // Get fields
  const authorField = fields.find(f => f.field === 'author');
  const titleField = fields.find(f => f.field === 'title');
  const origDateField = fields.find(f => f.field === 'original-date');
  const containerField = fields.find(f => f.field === 'container-title');
  const placeField = fields.find(f => f.field === 'publisher-place');
  const publisherField = fields.find(f => f.field === 'publisher');
  const issuedField = fields.find(f => f.field === 'issued');
  const volumeField = fields.find(f => f.field === 'volume');
  const pageField = fields.find(f => f.field === 'page');

  // Format author name for bibliography (inverted: "Surname, First Name")
  let authorDisplay = '';
  if (cslItem.author && cslItem.author.length > 0) {
    const firstAuthor = cslItem.author[0];
    if (firstAuthor.literal) {
      authorDisplay = firstAuthor.literal;
    } else if (firstAuthor.family) {
      authorDisplay = firstAuthor.family;
      if (firstAuthor.given) {
        authorDisplay += `, ${firstAuthor.given}`;
      }
    }
    // Add "et al." if more authors
    if (cslItem.author.length > 1) {
      authorDisplay += ' et al.';
    }
  }

  // Author (inverted format for bibliography)
  if (authorDisplay) {
    parts.push(`<span class="csl-field" data-field="author">${authorDisplay}</span>.`);
  }

  // Year after author
  if (issuedField) {
    parts.push(`<span class="csl-field" data-field="issued">${issuedField.displayValue}</span>.`);
  }

  // Title in italic with optional original date
  if (titleField) {
    let titlePart = `<i><span class="csl-field" data-field="title">${titleField.displayValue}</span></i>`;
    if (origDateField) {
      titlePart += ` (<span class="csl-field" data-field="original-date">${origDateField.displayValue}</span>)`;
    }
    parts.push(titlePart + '.');
  }

  // Container (journal/book) - for articles
  if (containerField) {
    let containerPart = `<i><span class="csl-field" data-field="container-title">${containerField.displayValue}</span></i>`;
    if (volumeField) {
      containerPart += ` <span class="csl-field" data-field="volume">${volumeField.displayValue}</span>`;
    }
    if (pageField) {
      containerPart += `: <span class="csl-field" data-field="page">${pageField.displayValue}</span>`;
    }
    parts.push(containerPart + '.');
  } else {
    // For books: Place: Publisher
    const pubParts: string[] = [];
    if (placeField) {
      pubParts.push(`<span class="csl-field" data-field="publisher-place">${placeField.displayValue}</span>`);
    }
    if (publisherField) {
      pubParts.push(`<span class="csl-field" data-field="publisher">${publisherField.displayValue}</span>`);
    }
    if (pubParts.length > 0) {
      parts.push(pubParts.join(': ') + '.');
    }
  }

  return parts.join(' ');
}

// ============================================================================
// CITATION PARSING
// ============================================================================

const citationPluginKey = new PluginKey("citation-plugin");

/**
 * Regex to match Pandoc citation patterns:
 * - [@key]
 * - [@key, p. 42]
 * - [@key; @key2]
 * - [see @key, p. 42]
 * - [@key, p. 42; also @key2, chap. 3]
 */
const CITATION_REGEX = /\[@([a-zA-Z0-9_:-]+)(?:,\s*([^\]]+))?\]/g;

/**
 * Regex to match bibliography placeholder (Mustache/Handlebars style)
 * Matches: {{bibliography}} or {{BIBLIOGRAPHY}}
 */
const BIBLIOGRAPHY_REGEX = /\{\{bibliography\}\}/gi;

/**
 * Extract all unique citation keys from a document
 */
function extractCitationKeys(doc: any): string[] {
  const keys = new Set<string>();

  doc.descendants((node: any) => {
    if (!node.isText) return;

    const text = node.text as string;
    let match;
    CITATION_REGEX.lastIndex = 0;

    while ((match = CITATION_REGEX.exec(text)) !== null) {
      keys.add(match[1]);
    }
  });

  return Array.from(keys);
}

/**
 * Load citation items for the document
 * First loads from local bibliography (IndexedDB), then fetches missing items from Zotero
 */
async function loadCitationItemsForDocument(doc: any): Promise<void> {
  const keys = extractCitationKeys(doc);

  if (keys.length === 0) {
    console.log('[citationPlugin] No citations found in document');
    return;
  }

  console.log('[citationPlugin] Found citation keys in document:', keys);

  // First, try to load items from local bibliography (IndexedDB)
  // Items are stored in native Zotero format - convert to CSL on-the-fly
  try {
    const { bibliographyService } = await import('../services/bibliographyService');
    const { selectedProject } = await import('../stores/appStore');
    const { get } = await import('svelte/store');

    const project = get(selectedProject);
    if (project) {
      const bibliography = await bibliographyService.getBibliography(project.id);
      if (bibliography && Object.keys(bibliography.items).length > 0) {
        // Register local items with citation service (converts Zotero→CSL on-the-fly)
        const localItems = Object.values(bibliography.items);
        await citationService.registerZoteroDataItems(localItems);
        console.log('[citationPlugin] Loaded', localItems.length, 'items from local bibliography (Zotero→CSL)');

        // Check which keys are still missing
        const localKeys = new Set(Object.keys(bibliography.items));
        const missingKeys = keys.filter(key => !localKeys.has(key));

        if (missingKeys.length === 0) {
          console.log('[citationPlugin] All citations found in local bibliography');
          return;
        }

        console.log('[citationPlugin] Missing keys not in local:', missingKeys);
      }
    }
  } catch (error) {
    console.warn('[citationPlugin] Could not load local bibliography:', error);
  }

  // Initialize Zotero if not already done (for missing items)
  if (!get(zoteroInitialized)) {
    console.log('[citationPlugin] Initializing Zotero for citation resolution');
    const success = await initZotero();
    if (!success) {
      console.error('[citationPlugin] Failed to initialize Zotero');
      return;
    }
  }

  // Load missing items from Zotero
  console.log('[citationPlugin] Loading items for citation keys from Zotero');
  const items = await loadItemsByKeys(keys);

  if (items.length > 0) {
    // Register items with citation service for citeproc-js rendering
    // registerItems skips items already registered (preserves local edits)
    citationService.registerItems(items);
    console.log('[citationPlugin] Registered', items.length, 'items from Zotero with citation service');
  }
}

/**
 * Parse a citation string into structured data
 */
function parseCitation(match: RegExpMatchArray): ParsedCitation {
  const key = match[1];
  const extra = match[2];

  let locator: string | undefined;
  let label: string | undefined;

  if (extra) {
    // Try to extract locator with label (e.g., "p. 42", "chap. 3")
    const locatorMatch = extra.match(/^(p\.|pp\.|ch\.|chap\.|sec\.|vol\.|no\.)?\s*(.+)$/i);
    if (locatorMatch) {
      if (locatorMatch[1]) {
        // Has explicit label
        const labelMap: Record<string, string> = {
          'p.': 'page',
          'pp.': 'page',
          'ch.': 'chapter',
          'chap.': 'chapter',
          'sec.': 'section',
          'vol.': 'volume',
          'no.': 'issue',
        };
        label = labelMap[locatorMatch[1].toLowerCase()] || 'page';
        locator = locatorMatch[2].trim();
      } else {
        // Just a locator, assume page
        label = 'page';
        locator = extra.trim();
      }
    }
  }

  return {
    key,
    locator,
    label,
    raw: match[0],
  };
}

// ============================================================================
// CROSS-CHAPTER CITATION PRIMING
// ============================================================================

/**
 * Citation extracted from markdown text (for cross-chapter priming)
 */
interface MarkdownCitation {
  key: string;
  locator?: string;
  label?: string;
  index: number;  // Position in the markdown (for ordering)
}

/**
 * Extract citations from raw markdown text
 * Used for priming citeproc with citations from previous chapters
 */
function extractCitationsFromMarkdown(markdown: string): MarkdownCitation[] {
  const citations: MarkdownCitation[] = [];
  let match;

  // Reset regex state
  const regex = new RegExp(CITATION_REGEX.source, 'g');

  while ((match = regex.exec(markdown)) !== null) {
    const parsed = parseCitation(match);
    citations.push({
      key: parsed.key,
      locator: parsed.locator,
      label: parsed.label,
      index: match.index,
    });
  }

  return citations;
}

/**
 * Load citations from previous chapters for priming citeproc
 * Returns the global noteIndex offset (number of citations in previous chapters)
 */
async function loadPreviousChaptersCitations(
  currentDocId: string
): Promise<{ primerCitations: Array<{ id: string; key: string; noteIndex: number; locator?: string; label?: string }>; noteIndexOffset: number }> {
  const orderedDocs = get(projectDocsOrdered);

  // Find current document index
  const currentIndex = orderedDocs.findIndex(doc => doc.id === currentDocId);

  if (currentIndex <= 0) {
    // No previous chapters or document not found
    return { primerCitations: [], noteIndexOffset: 0 };
  }

  console.log('[citationPlugin] Loading citations from', currentIndex, 'previous chapters');

  const primerCitations: Array<{ id: string; key: string; noteIndex: number; locator?: string; label?: string }> = [];
  let globalNoteIndex = 0;

  // Load citations from all previous chapters
  for (let i = 0; i < currentIndex; i++) {
    const priorDoc = orderedDocs[i];

    try {
      // Load document content from IndexedDB
      const content = await db.contents.get(priorDoc.id);

      if (!content) {
        console.log('[citationPlugin] No content for doc:', priorDoc.id);
        continue;
      }

      // Extract citations from markdown
      const citations = extractCitationsFromMarkdown(content);

      console.log('[citationPlugin] Found', citations.length, 'citations in chapter', i, ':', priorDoc.title);

      for (const citation of citations) {
        globalNoteIndex++;
        primerCitations.push({
          id: `${priorDoc.id}-cite-${citation.index}`,
          key: citation.key,
          noteIndex: globalNoteIndex,
          locator: citation.locator,
          label: citation.label,
        });
      }
    } catch (error) {
      console.warn('[citationPlugin] Error loading chapter', priorDoc.id, ':', error);
    }
  }

  console.log('[citationPlugin] Total primer citations:', primerCitations.length, 'noteIndexOffset:', globalNoteIndex);

  return { primerCitations, noteIndexOffset: globalNoteIndex };
}

// ============================================================================
// BIBLIOGRAPHY BLOCK RENDERING
// ============================================================================

/**
 * Cache for bibliography block data
 */
let bibliographyCache: { entries: string[]; keys: string[]; citedKeys: string[] } | null = null;

/**
 * Get all unique citation keys from the entire project
 */
async function getAllCitedKeysInProject(): Promise<string[]> {
  const orderedDocs = get(projectDocsOrdered);
  const allKeys = new Set<string>();

  for (const doc of orderedDocs) {
    try {
      const content = await db.contents.get(doc.id);
      if (content) {
        const citations = extractCitationsFromMarkdown(content);
        citations.forEach(c => allKeys.add(c.key));
      }
    } catch (error) {
      console.warn('[citationPlugin] Error loading doc for bibliography:', doc.id);
    }
  }

  return Array.from(allKeys);
}

/**
 * Inject data-field spans into citeproc HTML by matching field values
 * This preserves the CSL style while enabling inline editing
 */
function injectFieldSpansIntoCiteprocHtml(html: string, cslItem: any): string {
  if (!cslItem) return html;

  let result = html;

  // Get field values to search for
  const fieldsToMatch: Array<{ field: string; value: string; priority: number }> = [];

  // Title (high priority, usually unique)
  if (cslItem.title) {
    fieldsToMatch.push({ field: 'title', value: cslItem.title, priority: 1 });
  }

  // Container title (journal, book title)
  if (cslItem['container-title']) {
    fieldsToMatch.push({ field: 'container-title', value: cslItem['container-title'], priority: 2 });
  }

  // Publisher
  if (cslItem.publisher) {
    fieldsToMatch.push({ field: 'publisher', value: cslItem.publisher, priority: 3 });
  }

  // Publisher place
  if (cslItem['publisher-place']) {
    fieldsToMatch.push({ field: 'publisher-place', value: cslItem['publisher-place'], priority: 4 });
  }

  // Volume
  if (cslItem.volume) {
    fieldsToMatch.push({ field: 'volume', value: String(cslItem.volume), priority: 6 });
  }

  // Pages
  if (cslItem.page) {
    fieldsToMatch.push({ field: 'page', value: cslItem.page, priority: 7 });
  }

  // Year/Date (extract year from issued)
  if (cslItem.issued?.['date-parts']?.[0]?.[0]) {
    const year = String(cslItem.issued['date-parts'][0][0]);
    fieldsToMatch.push({ field: 'issued', value: year, priority: 5 });
  }

  // Authors (match full name patterns as they appear in bibliography output)
  // Try to match "Family, Given" or "Given Family" patterns
  if (cslItem.author && Array.isArray(cslItem.author)) {
    for (const author of cslItem.author) {
      if (author.literal) {
        // Literal name (organization, etc.)
        fieldsToMatch.push({ field: 'author', value: author.literal, priority: 0 });
      } else if (author.family) {
        // Try different name formats that CSL styles might produce
        const patterns: string[] = [];

        if (author.given) {
          // "Family, Given" (most common in bibliographies)
          patterns.push(`${author.family}, ${author.given}`);
          // "Family, Initial." (abbreviated)
          const initial = author.given.charAt(0);
          patterns.push(`${author.family}, ${initial}.`);
          patterns.push(`${author.family}, ${initial}`);
          // "Given Family" (some styles)
          patterns.push(`${author.given} ${author.family}`);
        }
        // Fall back to just family name
        patterns.push(author.family);

        // Use the longest pattern that exists in the HTML (priority 0 = processed first)
        for (const pattern of patterns) {
          if (pattern.length >= 2 && result.includes(pattern)) {
            fieldsToMatch.push({ field: 'author', value: pattern, priority: 0 });
            break; // Use first matching pattern (longest first)
          }
        }
      }
    }
  }

  // Sort by priority (process longer/unique values first to avoid partial matches)
  fieldsToMatch.sort((a, b) => a.priority - b.priority);

  // Track which parts of the HTML have been wrapped
  const wrappedRanges: Array<{ start: number; end: number }> = [];

  for (const { field, value } of fieldsToMatch) {
    if (!value || value.length < 2) continue; // Skip very short values

    // Escape special regex characters in the value
    const escapedValue = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Create a regex to find the value (case-insensitive, word boundaries when possible)
    const regex = new RegExp(`(?<!data-field="[^"]*)(${escapedValue})(?![^<]*>)`, 'gi');

    result = result.replace(regex, (match, captured, offset) => {
      // Check if this position overlaps with already wrapped content
      const matchEnd = offset + match.length;
      const overlaps = wrappedRanges.some(
        range => (offset >= range.start && offset < range.end) ||
                 (matchEnd > range.start && matchEnd <= range.end)
      );

      if (overlaps) return match;

      // Track this range as wrapped
      wrappedRanges.push({ start: offset, end: matchEnd + 50 }); // +50 for the span wrapper

      return `<span class="csl-field" data-field="${field}">${captured}</span>`;
    });
  }

  return result;
}

/**
 * Create a bibliography item badge with inline editable fields
 * Uses citeproc HTML with injected data-field spans for CSL styling + inline editing
 */
function createBibliographyItemBadge(
  formattedHtml: string,
  itemKey: string,
  cslItem: any,
  onSave: (field: string, newValue: any) => void
): HTMLElement {
  const item = document.createElement('div');
  item.className = 'bibliography-item';
  item.dataset.itemKey = itemKey;

  // Inject data-field spans into citeproc HTML for inline editing
  // This preserves CSL styling while enabling field detection
  if (cslItem) {
    const editableHtml = injectFieldSpansIntoCiteprocHtml(formattedHtml, cslItem);
    item.innerHTML = editableHtml;
    item.classList.add('bibliography-editable');

    // Add click handler for inline field editing (same as citation badges)
    item.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const target = e.target as Node;
      const targetElement = target.nodeType === Node.TEXT_NODE ? target.parentElement : target as HTMLElement;
      const clickedField = targetElement?.closest('.csl-field') as HTMLElement | null;

      // Enter edit mode on the item
      enterBibliographyEditMode(item, itemKey, cslItem, onSave);

      // If clicked on a field, open the inline editor
      if (clickedField) {
        handleBibliographyFieldClick(clickedField, item, itemKey, cslItem, onSave);
      }
    });
  } else {
    // Fallback to static HTML from citeproc
    item.innerHTML = formattedHtml;
  }

  return item;
}

/**
 * Track active bibliography edit state
 */
let activeBibEditItem: HTMLElement | null = null;
let activeBibEditInput: HTMLInputElement | null = null;
let activeBibEditField: HTMLElement | null = null;
let activeBibAuthorSelector: HTMLElement | null = null;

/**
 * Exit bibliography edit mode
 */
function exitBibliographyEditMode() {
  if (activeBibEditField) {
    activeBibEditField.classList.remove('csl-field-editing');
  }
  if (activeBibEditItem) {
    activeBibEditItem.classList.remove('bibliography-edit-mode');
    activeBibEditItem.querySelectorAll('.csl-field').forEach(field => {
      field.classList.remove('csl-field-highlight', 'csl-field-editing');
    });
  }
  if (activeBibEditInput && activeBibEditInput.parentElement) {
    activeBibEditInput.parentElement.removeChild(activeBibEditInput);
  }
  if (activeBibAuthorSelector && activeBibAuthorSelector.parentElement) {
    activeBibAuthorSelector.parentElement.removeChild(activeBibAuthorSelector);
  }

  activeBibEditItem = null;
  activeBibEditInput = null;
  activeBibEditField = null;
  activeBibAuthorSelector = null;
}

/**
 * Enter edit mode on a bibliography item
 */
function enterBibliographyEditMode(
  item: HTMLElement,
  itemKey: string,
  cslItem: any,
  onSave: (field: string, newValue: any) => void
) {
  // Exit any previous edit mode
  exitBibliographyEditMode();

  activeBibEditItem = item;
  item.classList.add('bibliography-edit-mode');

  // Highlight all editable fields
  const fields = item.querySelectorAll('.csl-field');
  fields.forEach((fieldSpan) => {
    const htmlField = fieldSpan as HTMLElement;
    htmlField.classList.add('csl-field-highlight');
  });
}

/**
 * Handle field click in bibliography edit mode
 */
function handleBibliographyFieldClick(
  fieldSpan: HTMLElement,
  item: HTMLElement,
  itemKey: string,
  cslItem: any,
  onSave: (field: string, newValue: any) => void
) {
  const field = fieldSpan.dataset.field;
  if (!field) return;

  // Close previous input/selector if any
  if (activeBibEditInput && activeBibEditInput.parentElement) {
    activeBibEditInput.parentElement.removeChild(activeBibEditInput);
    activeBibEditInput = null;
  }
  if (activeBibAuthorSelector && activeBibAuthorSelector.parentElement) {
    activeBibAuthorSelector.parentElement.removeChild(activeBibAuthorSelector);
    activeBibAuthorSelector = null;
  }
  if (activeBibEditField) {
    activeBibEditField.classList.remove('csl-field-editing');
  }

  // For author field, use the author selector (same as citation badges)
  if (field === 'author') {
    activeBibEditField = fieldSpan;
    // Reuse the createAuthorSelector function with a wrapper for exitBibliographyEditMode
    const wrappedOnSave = async (saveField: string, newValue: any) => {
      await onSave(saveField, newValue);
      exitBibliographyEditMode();
    };
    activeBibAuthorSelector = createAuthorSelector(fieldSpan, cslItem, itemKey, wrappedOnSave);
    return;
  }

  // Create inline input for other fields
  const rect = fieldSpan.getBoundingClientRect();
  const currentText = fieldSpan.textContent || '';

  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'citation-field-input bibliography-field-input';
  input.value = currentText;

  const textWidth = measureTextWidth(currentText, fieldSpan);
  const initialWidth = Math.max(textWidth + 16, 60);

  input.style.cssText = `
    position: fixed;
    left: ${rect.left - 4}px;
    top: ${rect.top - 2}px;
    width: ${initialWidth}px;
    height: ${rect.height + 4}px;
    font-size: ${getComputedStyle(fieldSpan).fontSize};
    font-family: ${getComputedStyle(fieldSpan).fontFamily};
    font-weight: ${getComputedStyle(fieldSpan).fontWeight};
    padding: 2px 6px;
    border: 2px solid var(--accent-color, #3b82f6);
    border-radius: 3px;
    background: white;
    z-index: 10000;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    outline: none;
  `;

  activeBibEditField = fieldSpan;
  activeBibEditInput = input;
  fieldSpan.classList.add('csl-field-editing');

  // Auto-resize
  input.addEventListener('input', () => {
    const newWidth = measureTextWidth(input.value || ' ', fieldSpan);
    input.style.width = `${Math.max(newWidth + 16, 60)}px`;
  });

  // Handle Enter/Escape
  input.addEventListener('keydown', async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const newValue = input.value.trim();
      if (newValue && newValue !== currentText) {
        await onSave(field, newValue);
      }
      exitBibliographyEditMode();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      exitBibliographyEditMode();
    }
  });

  // Handle blur
  input.addEventListener('blur', async () => {
    if (activeBibEditInput === input) {
      const newValue = input.value.trim();
      if (newValue && newValue !== currentText) {
        await onSave(field, newValue);
      }
      exitBibliographyEditMode();
    }
  });

  document.body.appendChild(input);

  requestAnimationFrame(() => {
    input.focus();
    input.select();
  });
}

/**
 * Create the bibliography block widget
 * This function awaits all async operations before returning the fully populated container
 */
async function createBibliographyBlockWidget(citedOnly: boolean = true): Promise<HTMLElement> {
  const container = document.createElement('div');
  container.className = 'bibliography-block';

  // No header - the user adds their own H1 for the bibliography section

  try {
    let keys: string[];

    if (citedOnly) {
      // Get only cited keys from the project
      keys = await getAllCitedKeysInProject();
      console.log('[citationPlugin] Bibliography: found', keys.length, 'cited keys in project');
    } else {
      // Get all keys from bibliography service
      const { bibliographyService } = await import('../services/bibliographyService');
      const { selectedProject } = await import('../stores/appStore');
      const project = get(selectedProject);
      if (project) {
        const bib = await bibliographyService.getBibliography(project.id);
        keys = bib ? Object.keys(bib.items) : [];
      } else {
        keys = [];
      }
    }

    if (keys.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'bibliography-loading';
      empty.textContent = 'Aucune référence citée';
      container.appendChild(empty);
      return container;
    }

    // Load items from bibliography service and register them in citationService
    const { bibliographyService, zoteroDataToCSL } = await import('../services/bibliographyService');
    const { selectedProject } = await import('../stores/appStore');
    const project = get(selectedProject);

    if (project) {
      const cslItems: any[] = [];
      for (const key of keys) {
        const zoteroItem = await bibliographyService.getItem(project.id, key);
        if (zoteroItem) {
          const cslItem = zoteroDataToCSL(zoteroItem);
          cslItems.push(cslItem);
        }
      }

      if (cslItems.length > 0) {
        console.log('[citationPlugin] Bibliography: registering', cslItems.length, 'items in citationService');
        await citationService.registerCSLItemsAsync(cslItems);
      }
    }

    // Generate bibliography using citeproc
    const bibResult = await citationService.makeBibliographyForKeys(keys);

    console.log('[citationPlugin] Bibliography: generated', bibResult.entries.length, 'entries');

    if (bibResult.entries.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'bibliography-loading';
      empty.textContent = 'Aucune référence trouvée dans la bibliographie';
      container.appendChild(empty);
      return container;
    }

    // Create list
    const list = document.createElement('div');
    list.className = 'bibliography-list';

    // Create save handler
    const createSaveHandler = (itemKey: string) => async (field: string, newValue: any) => {
      console.log('[citationPlugin] Bibliography save:', { itemKey, field, newValue });

      try {
        const { bibliographyService, zoteroDataToCSL } = await import('../services/bibliographyService');
        const { selectedProject } = await import('../stores/appStore');
        const project = get(selectedProject);

        if (!project) return;

        let zoteroItem = await bibliographyService.getItem(project.id, itemKey);
        if (!zoteroItem) return;

        zoteroItem = { ...zoteroItem };

        // Map CSL fields to Zotero fields
        const fieldMap: Record<string, string> = {
          'title': 'title',
          'container-title': 'publicationTitle',
          'publisher': 'publisher',
          'publisher-place': 'place',
          'issued': 'date',
          'page': 'pages',
          'volume': 'volume',
        };

        const zoteroField = fieldMap[field] || field;
        (zoteroItem as Record<string, unknown>)[zoteroField] = newValue;

        const result = await bibliographyService.updateItemAndPushToZotero(
          project.id,
          itemKey,
          zoteroItem
        );

        if (result.localSaved) {
          const cslItem = zoteroDataToCSL(zoteroItem);
          await citationService.registerCSLItemsAsync([cslItem]);
          // Invalidate cache to trigger refresh
          bibliographyCache = null;
        }
      } catch (error) {
        console.error('[citationPlugin] Bibliography save error:', error);
      }
    };

    // Render each item
    for (let i = 0; i < bibResult.entries.length; i++) {
      const entry = bibResult.entries[i];
      const key = bibResult.keys[i];
      const cslItem = citationService.getItem(key);

      const itemBadge = createBibliographyItemBadge(
        entry,
        key,
        cslItem,
        createSaveHandler(key)
      );
      list.appendChild(itemBadge);
    }

    container.appendChild(list);

    // Cache the result
    bibliographyCache = { ...bibResult, citedKeys: keys };

  } catch (error) {
    console.error('[citationPlugin] Error loading bibliography:', error);
    const errorDiv = document.createElement('div');
    errorDiv.className = 'bibliography-loading';
    errorDiv.textContent = 'Erreur de chargement';
    container.appendChild(errorDiv);
  }

  return container;
}

/**
 * Find bibliography placeholders in the document
 * Syntax: {{bibliography}} - shows only cited references
 */
function findBibliographyBlocks(doc: any): Array<{ from: number; to: number; citedOnly: boolean }> {
  const blocks: Array<{ from: number; to: number; citedOnly: boolean }> = [];

  doc.descendants((node: any, pos: number) => {
    if (!node.isText) return;

    const text = node.text as string;
    let match;

    // Reset regex state
    const regex = new RegExp(BIBLIOGRAPHY_REGEX.source, 'gi');

    while ((match = regex.exec(text)) !== null) {
      const from = pos + match.index;
      const to = from + match[0].length;

      // Always show only cited references (citedOnly = true)
      blocks.push({ from, to, citedOnly: true });
    }
  });

  return blocks;
}

// ============================================================================
// CSL STYLE LOADING
// ============================================================================

/**
 * Get the CSL style content from templates
 */
function getCslStyle(): string | null {
  const allTemplates = get(templates);
  const cslTemplate = allTemplates.find(
    (t: Template) => t.templateSection === 'csl' || t.templateType === 'csl' || t.name?.toLowerCase().includes('.csl')
  );

  if (cslTemplate?.content) {
    return cslTemplate.content;
  }

  return null;
}

// ============================================================================
// BADGE CREATION
// ============================================================================

/**
 * Citation state for badge rendering
 */
type CitationState = 'resolved' | 'pending' | 'unresolved';

/**
 * Currently active edit state (shared across all badges)
 */
let activeEditBadge: HTMLElement | null = null;
let activeEditInput: HTMLInputElement | null = null;
let activeEditField: HTMLElement | null = null; // Track the actual field element
let activeOutsideClickHandler: ((e: MouseEvent) => void) | null = null;

/**
 * Create a hidden span to measure text width
 */
function measureTextWidth(text: string, referenceElement: HTMLElement): number {
  const measureSpan = document.createElement('span');
  measureSpan.style.cssText = `
    position: absolute;
    visibility: hidden;
    white-space: pre;
    font-size: ${getComputedStyle(referenceElement).fontSize};
    font-family: ${getComputedStyle(referenceElement).fontFamily};
    font-weight: ${getComputedStyle(referenceElement).fontWeight};
    letter-spacing: ${getComputedStyle(referenceElement).letterSpacing};
  `;
  measureSpan.textContent = text;
  document.body.appendChild(measureSpan);
  const width = measureSpan.offsetWidth;
  document.body.removeChild(measureSpan);
  return width;
}

/**
 * Exit edit mode and cleanup
 */
function exitEditMode() {
  // Restore visibility of the edited field
  if (activeEditField) {
    activeEditField.classList.remove('csl-field-editing');
  }

  // Remove edit mode styling from badge
  if (activeEditBadge) {
    activeEditBadge.classList.remove('citation-edit-mode');
    // Remove highlight from all fields
    activeEditBadge.querySelectorAll('.csl-field').forEach(field => {
      field.classList.remove('csl-field-highlight', 'csl-field-editing');
    });
    // Remove any locator container (input) - but keep the "+" button
    const locatorContainer = activeEditBadge.querySelector('.citation-locator-container');
    if (locatorContainer) {
      locatorContainer.remove();
    }
  }

  // Remove the text input
  if (activeEditInput && activeEditInput.parentElement) {
    activeEditInput.parentElement.removeChild(activeEditInput);
  }

  // Remove the author selector
  if (activeAuthorSelector && activeAuthorSelector.parentElement) {
    activeAuthorSelector.parentElement.removeChild(activeAuthorSelector);
  }

  // Remove the locator selector
  if (activeLocatorSelector && activeLocatorSelector.parentElement) {
    activeLocatorSelector.parentElement.removeChild(activeLocatorSelector);
  }

  // Remove outside click handler
  if (activeOutsideClickHandler) {
    document.removeEventListener('click', activeOutsideClickHandler);
    activeOutsideClickHandler = null;
  }

  activeEditBadge = null;
  activeEditInput = null;
  activeEditField = null;
  activeAuthorSelector = null;
  activeLocatorSelector = null;
  activeLocatorInput = null;
}

/**
 * Create inline input for field editing with auto-width
 */
function createInlineInput(
  fieldSpan: HTMLElement,
  field: string,
  citationKey: string,
  onSave: (field: string, newValue: string) => void
): HTMLInputElement {
  const rect = fieldSpan.getBoundingClientRect();
  const currentText = fieldSpan.textContent || '';

  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'citation-field-input';
  input.value = currentText;

  // Calculate initial width based on text
  const textWidth = measureTextWidth(currentText, fieldSpan);
  const initialWidth = Math.max(textWidth + 16, 60); // Add padding, min 60px

  // Position the input over the field
  input.style.cssText = `
    position: fixed;
    left: ${rect.left - 4}px;
    top: ${rect.top - 2}px;
    width: ${initialWidth}px;
    height: ${rect.height + 4}px;
    font-size: ${getComputedStyle(fieldSpan).fontSize};
    font-family: ${getComputedStyle(fieldSpan).fontFamily};
    font-weight: ${getComputedStyle(fieldSpan).fontWeight};
    padding: 2px 6px;
    border: 2px solid var(--accent-color, #3b82f6);
    border-radius: 3px;
    background: white;
    z-index: 10000;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    outline: none;
  `;

  // Track the field being edited
  activeEditField = fieldSpan;
  fieldSpan.classList.add('csl-field-editing');

  // Auto-resize input as user types
  const resizeInput = () => {
    const newWidth = measureTextWidth(input.value || ' ', fieldSpan);
    input.style.width = `${Math.max(newWidth + 16, 60)}px`;
  };
  input.addEventListener('input', resizeInput);

  // Handle save on Enter
  input.addEventListener('keydown', async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const newValue = input.value.trim();
      console.log('[citationPlugin] Enter pressed, saving field:', field, 'value:', newValue, 'original:', currentText);
      if (newValue && newValue !== currentText) {
        await onSave(field, newValue);
      }
      exitEditMode();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      exitEditMode();
    } else if (e.key === 'Tab') {
      // TODO: Could implement tab to next field
      e.preventDefault();
      e.stopPropagation();
      exitEditMode();
    }
  });

  // Handle blur - save and exit
  input.addEventListener('blur', async () => {
    console.log('[citationPlugin] Blur event, activeEditInput === input:', activeEditInput === input);
    // Check if this input is still the active one
    if (activeEditInput === input) {
      const newValue = input.value.trim();
      console.log('[citationPlugin] Blur - field:', field, 'newValue:', newValue, 'currentText:', currentText, 'different:', newValue !== currentText);
      if (newValue && newValue !== currentText) {
        await onSave(field, newValue);
      } else {
        console.log('[citationPlugin] Blur - NOT saving because:', !newValue ? 'empty value' : 'same as original');
      }
      exitEditMode();
    }
  });

  document.body.appendChild(input);

  // Focus and select after a tick to ensure it's in the DOM
  requestAnimationFrame(() => {
    input.focus();
    input.select();
  });

  return input;
}

/**
 * Zotero creator types available for selection
 */
const CREATOR_TYPES = [
  { value: 'author', label: 'Author' },
  { value: 'contributor', label: 'Contributor' },
  { value: 'editor', label: 'Editor' },
  { value: 'seriesEditor', label: 'Series Editor' },
  { value: 'translator', label: 'Translator' },
];

/**
 * Locator types for citation references (page, chapter, paragraph)
 */
const LOCATOR_TYPES = [
  { value: 'page', label: 'Page', abbrev: 'p.', placeholder: '42' },
  { value: 'pages', label: 'Pages', abbrev: 'pp.', placeholder: '42-45' },
  { value: 'chapter', label: 'Chapitre', abbrev: 'chap.', placeholder: '3' },
  { value: 'paragraph', label: 'Paragraphe', abbrev: '§', placeholder: '12' },
];

/**
 * Track active author selector
 */
let activeAuthorSelector: HTMLElement | null = null;

/**
 * Track active locator selector
 */
let activeLocatorSelector: HTMLElement | null = null;

/**
 * Track active locator input
 */
let activeLocatorInput: HTMLInputElement | null = null;

/**
 * Store reference to editor view for markdown updates
 */
let globalEditorView: any = null;

/**
 * Create author selector dropdown for editing authors
 */
function createAuthorSelector(
  fieldSpan: HTMLElement,
  cslItem: any,
  citationKey: string,
  onSave: (field: string, newValue: any) => void
): HTMLElement {
  const rect = fieldSpan.getBoundingClientRect();

  // Get all creators from the CSL item (authors, editors, etc.)
  const creators: Array<{ given?: string; family?: string; literal?: string; type: string }> = [];

  // Collect all creator types from CSL item
  const creatorFields = ['author', 'editor', 'translator', 'contributor', 'collection-editor'];
  for (const creatorField of creatorFields) {
    const items = cslItem[creatorField];
    if (items && Array.isArray(items)) {
      for (const item of items) {
        creators.push({
          ...item,
          type: creatorField === 'collection-editor' ? 'seriesEditor' : creatorField
        });
      }
    }
  }

  // Create the dropdown container
  const dropdown = document.createElement('div');
  dropdown.className = 'author-selector-dropdown';
  dropdown.style.cssText = `
    position: fixed;
    left: ${rect.left}px;
    top: ${rect.bottom + 4}px;
    min-width: 200px;
    max-width: 350px;
    background: var(--panel-background, #fff);
    color: var(--text-primary, #1f2937);
    border: 1px solid var(--border-color, #e5e7eb);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    z-index: 10000;
    overflow: visible;
  `;

  // Track which submenu is currently visible
  let activeSubMenu: HTMLElement | null = null;

  // Create list of authors
  creators.forEach((creator, index) => {
    const fullName = creator.literal
      ? creator.literal
      : `${creator.given || ''} ${creator.family || ''}`.trim();

    const creatorType = CREATOR_TYPES.find(t => t.value === creator.type) || CREATOR_TYPES[0];

    const item = document.createElement('div');
    item.className = 'author-selector-item';
    item.style.cssText = `
      padding: 8px 12px;
      cursor: pointer;
      display: flex;
      justify-content: space-between;
      align-items: center;
      position: relative;
      border-bottom: 1px solid var(--border-color-light, #f3f4f6);
      color: var(--text-primary, #1f2937);
    `;

    item.innerHTML = `
      <span class="author-name" style="font-weight: 500; color: var(--text-primary, #1f2937);">${fullName}</span>
      <span class="author-type" style="font-size: 0.8em; color: var(--text-muted, #6b7280);">${creatorType.label} ▸</span>
    `;

    // Create sub-menu for type selection
    const subMenu = document.createElement('div');
    subMenu.className = 'author-type-submenu';
    subMenu.style.cssText = `
      position: absolute;
      left: 100%;
      top: 0;
      min-width: 140px;
      background: var(--panel-background, #fff);
      color: var(--text-primary, #1f2937);
      border: 1px solid var(--border-color, #e5e7eb);
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      display: none;
      z-index: 10001;
    `;

    CREATOR_TYPES.forEach((type) => {
      const typeOption = document.createElement('div');
      typeOption.className = 'author-type-option';
      typeOption.style.cssText = `
        padding: 8px 12px;
        cursor: pointer;
        font-size: 0.9em;
        color: var(--text-primary, #1f2937);
      `;
      typeOption.textContent = type.label;

      if (type.value === creator.type) {
        typeOption.style.fontWeight = '600';
        typeOption.style.color = 'var(--accent-color, #3b82f6)';
      }

      typeOption.addEventListener('mouseenter', () => {
        typeOption.style.background = 'var(--hover-background, #f3f4f6)';
      });
      typeOption.addEventListener('mouseleave', () => {
        typeOption.style.background = '';
      });

      typeOption.addEventListener('click', async (e) => {
        e.stopPropagation();

        // Update the creator type
        const updatedCreators = [...creators];
        updatedCreators[index] = { ...creator, type: type.value };

        // Convert back to CSL format grouped by type
        const cslCreators: Record<string, any[]> = {};
        for (const c of updatedCreators) {
          const cslType = c.type === 'seriesEditor' ? 'collection-editor' : c.type;
          if (!cslCreators[cslType]) cslCreators[cslType] = [];
          const { type: _, ...nameOnly } = c;
          cslCreators[cslType].push(nameOnly);
        }

        await onSave('creators', cslCreators);
        exitEditMode(); // Exit edit mode to allow decoration rebuild
      });

      subMenu.appendChild(typeOption);
    });

    item.appendChild(subMenu);

    // Show sub-menu on click
    item.addEventListener('click', (e) => {
      e.stopPropagation();

      // Hide any other active submenu
      if (activeSubMenu && activeSubMenu !== subMenu) {
        activeSubMenu.style.display = 'none';
      }

      // Toggle this submenu
      if (subMenu.style.display === 'block') {
        subMenu.style.display = 'none';
        activeSubMenu = null;
      } else {
        subMenu.style.display = 'block';
        activeSubMenu = subMenu;
      }
    });

    // Highlight on hover
    item.addEventListener('mouseenter', () => {
      item.style.background = 'var(--hover-background, #f3f4f6)';
    });
    item.addEventListener('mouseleave', () => {
      item.style.background = '';
    });

    dropdown.appendChild(item);
  });

  // Add to document
  document.body.appendChild(dropdown);

  // Track the field being edited (but don't hide it - dropdown appears below)
  activeEditField = fieldSpan;

  // Outside click is handled by enterEditMode's activeOutsideClickHandler

  return dropdown;
}

/**
 * Close the author selector
 */
function closeAuthorSelector() {
  if (activeAuthorSelector && activeAuthorSelector.parentElement) {
    activeAuthorSelector.parentElement.removeChild(activeAuthorSelector);
  }
  if (activeEditField) {
    activeEditField.classList.remove('csl-field-editing');
  }
  activeAuthorSelector = null;
  activeEditField = null;
}

/**
 * Close the locator selector
 */
function closeLocatorSelector() {
  if (activeLocatorSelector && activeLocatorSelector.parentElement) {
    activeLocatorSelector.parentElement.removeChild(activeLocatorSelector);
  }
  if (activeLocatorInput && activeLocatorInput.parentElement) {
    activeLocatorInput.parentElement.removeChild(activeLocatorInput);
  }
  activeLocatorSelector = null;
  activeLocatorInput = null;
}

/**
 * Update the citation markdown in the document with a new locator
 */
function updateCitationMarkdownWithLocator(
  citationKey: string,
  locatorType: string,
  locatorValue: string,
  citationFrom: number,
  citationTo: number
): void {
  console.log('[citationPlugin] updateCitationMarkdownWithLocator called:', {
    citationKey,
    locatorType,
    locatorValue,
    citationFrom,
    citationTo,
    hasEditorView: !!globalEditorView
  });

  if (!globalEditorView) {
    console.error('[citationPlugin] No editor view available for markdown update');
    return;
  }

  const locatorInfo = LOCATOR_TYPES.find(t => t.value === locatorType);
  if (!locatorInfo) {
    console.error('[citationPlugin] Unknown locator type:', locatorType);
    return;
  }

  // Build the new citation markdown
  const newCitation = `[@${citationKey}, ${locatorInfo.abbrev} ${locatorValue}]`;

  try {
    const currentText = globalEditorView.state.doc.textBetween(citationFrom, citationTo);
    console.log('[citationPlugin] Updating citation markdown:', {
      from: citationFrom,
      to: citationTo,
      newCitation,
      currentText,
      docSize: globalEditorView.state.doc.content.size
    });

    // Replace the old citation text with the new one
    const { state, dispatch } = globalEditorView;
    const tr = state.tr.insertText(newCitation, citationFrom, citationTo);
    dispatch(tr);

    console.log('[citationPlugin] Citation updated successfully');
  } catch (error) {
    console.error('[citationPlugin] Error updating citation:', error);
  }
}

/**
 * Create locator selector dropdown for adding page/chapter/paragraph references
 */
function createLocatorSelector(
  badge: HTMLElement,
  citationKey: string,
  citationFrom: number,
  citationTo: number,
  currentLocator?: string,
  currentLabel?: string
): HTMLElement {
  const rect = badge.getBoundingClientRect();

  // Create the dropdown container
  const dropdown = document.createElement('div');
  dropdown.className = 'locator-selector-dropdown';
  dropdown.style.cssText = `
    position: fixed;
    left: ${rect.right + 4}px;
    top: ${rect.top}px;
    min-width: 140px;
    background: var(--panel-background, #fff);
    color: var(--text-primary, #1f2937);
    border: 1px solid var(--border-color, #e5e7eb);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    z-index: 10000;
    overflow: visible;
  `;

  // Create list of locator types
  LOCATOR_TYPES.forEach((locatorType) => {
    const item = document.createElement('div');
    item.className = 'locator-selector-item';
    item.style.cssText = `
      padding: 8px 12px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
      border-bottom: 1px solid var(--border-color-light, #f3f4f6);
      color: var(--text-primary, #1f2937);
    `;

    item.innerHTML = `
      <span class="locator-abbrev" style="font-weight: 600; color: var(--accent-color, #3b82f6); min-width: 35px;">${locatorType.abbrev}</span>
      <span class="locator-label" style="color: var(--text-primary, #1f2937);">${locatorType.label}</span>
    `;

    // Highlight on hover
    item.addEventListener('mouseenter', () => {
      item.style.background = 'var(--hover-background, #f3f4f6)';
    });
    item.addEventListener('mouseleave', () => {
      item.style.background = '';
    });

    // Handle click - show input for locator value
    item.addEventListener('click', (e) => {
      e.stopPropagation();

      // Close the dropdown
      if (dropdown.parentElement) {
        dropdown.parentElement.removeChild(dropdown);
      }
      activeLocatorSelector = null;

      // Create input for the locator value
      showLocatorInput(badge, citationKey, locatorType, citationFrom, citationTo);
    });

    dropdown.appendChild(item);
  });

  // Add to document
  document.body.appendChild(dropdown);
  activeLocatorSelector = dropdown;

  return dropdown;
}

/**
 * Show input field for entering locator value (page number, etc.)
 */
function showLocatorInput(
  badge: HTMLElement,
  citationKey: string,
  locatorType: { value: string; label: string; abbrev: string; placeholder: string },
  citationFrom: number,
  citationTo: number
): void {
  const rect = badge.getBoundingClientRect();

  // Create a container for the locator display
  const container = document.createElement('span');
  container.className = 'citation-locator-container';
  container.style.cssText = `
    display: inline-flex;
    align-items: center;
    margin-left: 4px;
  `;

  // Add the abbreviation label
  const abbrevSpan = document.createElement('span');
  abbrevSpan.className = 'citation-locator-abbrev';
  abbrevSpan.textContent = `, ${locatorType.abbrev} `;
  abbrevSpan.style.cssText = `
    color: var(--text-primary, #1f2937);
  `;
  container.appendChild(abbrevSpan);

  // Create the input
  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'citation-locator-input';
  input.placeholder = locatorType.placeholder;
  input.style.cssText = `
    width: 50px;
    padding: 2px 4px;
    border: 2px solid var(--accent-color, #3b82f6);
    border-radius: 3px;
    background: white;
    font-size: inherit;
    font-family: inherit;
    outline: none;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  `;

  container.appendChild(input);

  // Insert the container at the end of the badge (before the period)
  // First, remove any existing locator container
  const existingContainer = badge.querySelector('.citation-locator-container');
  if (existingContainer) {
    existingContainer.remove();
  }

  // Find the last text node with a period and insert before it
  badge.appendChild(container);

  activeLocatorInput = input;

  // Handle Enter to save
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const value = input.value.trim();
      if (value) {
        updateCitationMarkdownWithLocator(citationKey, locatorType.value, value, citationFrom, citationTo);
      }
      // Clean up
      container.remove();
      activeLocatorInput = null;
      exitEditMode();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      container.remove();
      activeLocatorInput = null;
    }
  });

  // Handle blur
  input.addEventListener('blur', () => {
    const value = input.value.trim();
    if (value) {
      updateCitationMarkdownWithLocator(citationKey, locatorType.value, value, citationFrom, citationTo);
    }
    // Clean up after a small delay to allow click events to process
    setTimeout(() => {
      if (container.parentElement) {
        container.remove();
      }
      activeLocatorInput = null;
    }, 100);
  });

  // Focus the input
  requestAnimationFrame(() => {
    input.focus();
  });
}

/**
 * Handle field click in edit mode
 */
function handleFieldClick(
  fieldSpan: HTMLElement,
  badge: HTMLElement,
  citationKey: string,
  cslItem: any,
  onSave: (field: string, newValue: any) => void
) {
  const field = fieldSpan.dataset.field;
  if (!field) return;

  // If clicking on a different field, close previous editor first
  if (activeEditField !== fieldSpan) {
    // Close author selector if open
    if (activeAuthorSelector) {
      closeAuthorSelector();
    }
    // Close text input if open
    if (activeEditInput) {
      if (activeEditField) {
        activeEditField.classList.remove('csl-field-editing');
      }
      if (activeEditInput.parentElement) {
        activeEditInput.parentElement.removeChild(activeEditInput);
      }
      activeEditInput = null;
    }
  }

  // For author field, use the author selector
  if (field === 'author') {
    activeAuthorSelector = createAuthorSelector(fieldSpan, cslItem, citationKey, onSave);
  } else {
    // Create text input for other fields
    activeEditInput = createInlineInput(fieldSpan, field, citationKey, onSave);
  }
}

/**
 * Enter edit mode on a badge
 */
function enterEditMode(
  badge: HTMLElement,
  citationKey: string,
  cslItem: any,
  onSave: (field: string, newValue: any) => void,
  citationFrom?: number,
  citationTo?: number
) {
  // Exit any previous edit mode
  exitEditMode();

  activeEditBadge = badge;
  badge.classList.add('citation-edit-mode');

  // Highlight all editable fields
  const fields = badge.querySelectorAll('.csl-field');
  fields.forEach((fieldSpan) => {
    const htmlField = fieldSpan as HTMLElement;
    htmlField.classList.add('csl-field-highlight');

    // Add click listener for editing
    const clickHandler = (e: Event) => {
      e.stopPropagation();
      e.preventDefault();
      handleFieldClick(htmlField, badge, citationKey, cslItem, onSave);
    };
    htmlField.addEventListener('click', clickHandler);
  });

  // Exit edit mode on click outside (with delay to avoid immediate trigger)
  activeOutsideClickHandler = (e: MouseEvent) => {
    const target = e.target as Node;
    // Don't exit if clicking on the badge, a field, the input, the author selector, or the locator selector
    if (
      badge.contains(target) ||
      (activeEditInput && activeEditInput.contains(target)) ||
      (activeAuthorSelector && activeAuthorSelector.contains(target)) ||
      (activeLocatorSelector && activeLocatorSelector.contains(target)) ||
      (activeLocatorInput && activeLocatorInput.contains(target))
    ) {
      return;
    }
    // Blur the input to trigger save via blur handler, don't call exitEditMode directly
    // This avoids race condition where exitEditMode clears activeEditInput before blur saves
    if (activeEditInput) {
      activeEditInput.blur();
    } else if (activeLocatorInput) {
      activeLocatorInput.blur();
    } else {
      exitEditMode();
    }
  };

  setTimeout(() => {
    if (activeOutsideClickHandler) {
      document.addEventListener('click', activeOutsideClickHandler);
    }
  }, 100);
}

/**
 * Check if formatted text is a short-form citation (ibid., op. cit., etc.)
 * These should NOT use field-aware rendering.
 *
 * Handles various formats:
 * - Plain: "Ibid.", "op. cit."
 * - With HTML: "<i>Ibid.</i>", "<span class='...'>Ibid.</span>"
 * - With locator: "Ibid., p. 25", "op. cit., p. 30-35"
 * - With author: "Author, op. cit., p. 30"
 */
function isShortFormCitation(formattedText: string): boolean {
  // Strip HTML tags for cleaner matching
  const stripped = formattedText.replace(/<[^>]+>/g, '').trim();

  // Match short-form patterns at the start or after author name
  // Patterns can be followed by punctuation, locators, or end of string
  const shortForms = [
    /\bibid\.?/i,
    /\bid\.?\b/i,
    /\bidem\.?/i,
    /\bop\.?\s*cit\.?/i,
    /\bloc\.?\s*cit\.?/i,
    /\bart\.?\s*cit\.?/i,
    /\bsupra\b/i,
    /\binfra\b/i,
  ];

  return shortForms.some(re => re.test(stripped));
}

/**
 * Create a citation badge DOM element with editable fields
 */
function createCitationBadge(
  citation: ParsedCitation,
  formattedText: string,
  state: CitationState,
  cslItem?: any,
  citationFrom?: number,
  citationTo?: number
): HTMLElement {
  const badge = document.createElement("span");
  badge.classList.add("citation-badge");
  badge.contentEditable = "false";

  switch (state) {
    case 'resolved':
      badge.classList.add("citation-resolved");

      // Check if citeproc returned a short-form citation (ibid., op. cit., etc.)
      // In that case, use the citeproc output directly, not field-aware rendering
      if (isShortFormCitation(formattedText)) {
        badge.innerHTML = formattedText;
        badge.classList.add("citation-short-form");
        // No click handler for short-form citations (can't edit ibid.)
        break;
      }

      if (cslItem) {
        // Use field-aware rendering for full citations
        let html = generateEditableHTML(cslItem);
        // Append locator (page number) if present
        if (citation.locator) {
          // Remove trailing period if present, add locator, then add period back
          html = html.replace(/\.\s*$/, '');
          const label = citation.label || 'page';
          const labelAbbrev = label === 'page' ? 'p.' : label === 'chapter' ? 'chap.' : label + '.';
          html += `, <span class="csl-field csl-locator" data-field="locator">${labelAbbrev} ${citation.locator}</span>.`;
        }
        badge.innerHTML = html;

        // Add "+" button for locator (visible on hover)
        if (citationFrom !== undefined && citationTo !== undefined) {
          const addLocatorBtn = document.createElement('span');
          addLocatorBtn.className = 'add-locator-btn';
          addLocatorBtn.textContent = '+';
          addLocatorBtn.contentEditable = 'false';
          addLocatorBtn.title = 'Ajouter page, chapitre, paragraphe...';

          addLocatorBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            e.preventDefault();

            // Close any existing locator selector
            closeLocatorSelector();

            // Show the locator type selector
            createLocatorSelector(badge, citation.key, citationFrom, citationTo);
          });

          badge.appendChild(addLocatorBtn);
        }
      } else {
        // Fallback to citeproc output
        badge.innerHTML = formattedText;
      }

      // Add click to enter edit mode (and directly edit if clicking on a field)
      badge.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Check if click was directly on a field
        // Handle both element clicks and text node clicks
        const target = e.target as Node;
        const targetElement = target.nodeType === Node.TEXT_NODE ? target.parentElement : target as HTMLElement;
        const clickedField = targetElement?.closest('.csl-field') as HTMLElement | null;

        const onSave = async (field: string, newValue: any) => {
          console.log('[citationPlugin] onSave called:', { key: citation.key, field, newValue });

          try {
            // Import services dynamically to avoid circular deps
            const { bibliographyService, zoteroDataToCSL } = await import('../services/bibliographyService');
            const { selectedProject } = await import('../stores/appStore');
            const { get } = await import('svelte/store');

            const project = get(selectedProject);
            if (!project) {
              console.error('[citationPlugin] No project selected');
              return;
            }

            // Get current item in native Zotero format from bibliography
            let zoteroItem = await bibliographyService.getItem(project.id, citation.key);
            if (!zoteroItem) {
              console.error('[citationPlugin] Item not found in bibliography:', citation.key);
              return;
            }

            // Clone the item to avoid mutating the cached version
            zoteroItem = { ...zoteroItem };

            // Map CSL field names to Zotero field names
            const cslToZoteroFieldMap: Record<string, string> = {
              'title': 'title',
              'container-title': 'publicationTitle',
              'publisher': 'publisher',
              'publisher-place': 'place',
              'issued': 'date',
              'page': 'pages',
              'volume': 'volume',
              'issue': 'issue',
              'edition': 'edition',
              'DOI': 'DOI',
              'ISBN': 'ISBN',
              'ISSN': 'ISSN',
              'URL': 'url',
            };

            // Update the field directly in Zotero format
            if (field === 'creators') {
              // Convert CSL creator format to Zotero creators array
              const zoteroCreators: Array<{ creatorType: string; firstName?: string; lastName?: string; name?: string }> = [];
              for (const [cslRole, names] of Object.entries(newValue)) {
                const zoteroRole = cslRole === 'collection-editor' ? 'seriesEditor' : cslRole;
                for (const n of names as Array<{ given?: string; family?: string; literal?: string }>) {
                  if (n.literal) {
                    zoteroCreators.push({ creatorType: zoteroRole, name: n.literal });
                  } else {
                    zoteroCreators.push({ creatorType: zoteroRole, firstName: n.given || '', lastName: n.family || '' });
                  }
                }
              }
              zoteroItem.creators = zoteroCreators;
            } else if (field === 'issued') {
              // Date field maps to 'date' in Zotero
              zoteroItem.date = newValue;
            } else {
              // Map other CSL fields to Zotero fields
              const zoteroField = cslToZoteroFieldMap[field] || field;
              (zoteroItem as Record<string, unknown>)[zoteroField] = newValue;
            }

            // Ensure the item has the correct key
            zoteroItem.key = citation.key;

            console.log('[citationPlugin] Zotero item after update:', JSON.stringify(zoteroItem).substring(0, 200));

            // Use the unified update function that handles local save + Zotero push
            const result = await bibliographyService.updateItemAndPushToZotero(
              project.id,
              citation.key,
              zoteroItem
            );

            console.log('[citationPlugin] Update result:', result);

            if (result.localSaved) {
              // Convert updated Zotero item to CSL for citation service refresh
              const cslItem = zoteroDataToCSL(zoteroItem);
              await citationService.registerCSLItemsAsync([cslItem]);
              console.log('[citationPlugin] Citation service updated with converted CSL');
            }

            if (result.error) {
              console.warn('[citationPlugin] Update had error:', result.error);
            }
          } catch (error) {
            console.error('[citationPlugin] Error saving field:', error);
          }
        };

        // Enter edit mode with position info for locator addition
        enterEditMode(badge, citation.key, cslItem, onSave, citationFrom, citationTo);

        // If clicked directly on a field, immediately open the editor
        if (clickedField) {
          handleFieldClick(clickedField, badge, citation.key, cslItem, onSave);
        }
      });
      break;

    case 'pending':
      badge.classList.add("citation-pending");
      badge.textContent = `@${citation.key}`;
      break;

    case 'unresolved':
      badge.classList.add("citation-unresolved");
      badge.textContent = `@${citation.key}`;
      break;
  }

  // Add data attributes for potential interactions
  badge.dataset.citationKey = citation.key;
  badge.dataset.state = state;
  if (citation.locator) {
    badge.dataset.locator = citation.locator;
  }
  if (citationFrom !== undefined) {
    badge.dataset.citationFrom = String(citationFrom);
  }
  if (citationTo !== undefined) {
    badge.dataset.citationTo = String(citationTo);
  }

  return badge;
}

// ============================================================================
// DECORATION BUILDING
// ============================================================================

/**
 * Cache for position-aware formatted citations
 * Key: citation occurrence ID (position-based)
 * Value: formatted citation text (may include "ibid.", "op. cit.", etc.)
 */
let positionAwareCitationCache: Map<string, string> = new Map();

/**
 * Flag to track if position-aware processing is in progress
 */
let isProcessingCitations = false;

/**
 * Collect all citations from document in order
 */
interface DocumentCitation {
  id: string;           // Unique ID for this occurrence (e.g., "cite-123")
  key: string;          // Item key (e.g., "durkheim1897")
  noteIndex: number;    // Position in document (1-based)
  locator?: string;
  label?: string;
  from: number;         // Position in ProseMirror doc
  to: number;
  raw: string;          // Original text (e.g., "[@key]")
}

function collectDocumentCitations(doc: any): DocumentCitation[] {
  const citations: DocumentCitation[] = [];
  let noteIndex = 0;

  doc.descendants((node: any, pos: number) => {
    if (!node.isText) return;

    const text = node.text as string;
    let match;
    CITATION_REGEX.lastIndex = 0;

    while ((match = CITATION_REGEX.exec(text)) !== null) {
      noteIndex++;
      const parsed = parseCitation(match);
      const from = pos + match.index;
      const to = from + match[0].length;

      citations.push({
        id: `cite-${from}`,  // Use position as unique ID
        key: parsed.key,
        noteIndex,
        locator: parsed.locator,
        label: parsed.label,
        from,
        to,
        raw: match[0],
      });
    }
  });

  return citations;
}

/**
 * Process all document citations for position-aware formatting (ibid., op. cit., etc.)
 * This is async and updates the positionAwareCitationCache
 *
 * Cross-chapter priming: When processing citations, we first load citations from
 * all previous chapters in the project. This allows citeproc-js to generate
 * correct ibid./op.cit. references that span across chapters.
 */
async function processDocumentCitationsAsync(doc: any, editorView: any): Promise<void> {
  if (isProcessingCitations) {
    console.log('[citationPlugin] Already processing citations, skipping');
    return;
  }

  isProcessingCitations = true;

  try {
    const documentCitations = collectDocumentCitations(doc);

    if (documentCitations.length === 0) {
      positionAwareCitationCache.clear();
      return;
    }

    console.log('[citationPlugin] Processing', documentCitations.length, 'citations for position-aware formatting');

    // Get current document ID
    const docId = get(currentDocId);

    // Load citations from previous chapters for cross-chapter ibid/op.cit. support
    let primerCitations: Array<{ id: string; key: string; noteIndex: number; locator?: string; label?: string }> = [];
    let noteIndexOffset = 0;

    if (docId && !docId.startsWith('template:')) {
      const priming = await loadPreviousChaptersCitations(docId);
      primerCitations = priming.primerCitations;
      noteIndexOffset = priming.noteIndexOffset;
    }

    // Convert current document citations to format expected by citationService
    // Apply noteIndex offset to account for citations in previous chapters
    const currentDocCitations = documentCitations.map(c => ({
      id: c.id,
      key: c.key,
      noteIndex: noteIndexOffset + c.noteIndex,  // Offset by prior chapters
      locator: c.locator,
      label: c.label,
    }));

    // Combine primer citations with current document citations
    const allCitations = [...primerCitations, ...currentDocCitations];

    console.log('[citationPlugin] Processing with cross-chapter context:', {
      primerCount: primerCitations.length,
      currentCount: currentDocCitations.length,
      totalCount: allCitations.length,
      noteIndexOffset,
    });

    // Process all citations together for position awareness
    const results = await citationService.processDocumentCitations(allCitations);

    // Only cache results for the current document (not primer citations)
    const currentDocResults = new Map<string, string>();
    for (const c of currentDocCitations) {
      const formatted = results.get(c.id);
      if (formatted !== undefined) {
        currentDocResults.set(c.id, formatted);
      }
    }

    // Update the cache with only current document results
    positionAwareCitationCache = currentDocResults;

    console.log('[citationPlugin] Position-aware cache updated with', currentDocResults.size, 'entries (cross-chapter aware)');

    // Trigger decoration rebuild
    if (editorView) {
      const tr = editorView.state.tr.setMeta(citationPluginKey, { positionAwareUpdate: true });
      editorView.dispatch(tr);
    }

  } catch (error) {
    console.error('[citationPlugin] Error processing citations:', error);
  } finally {
    isProcessingCitations = false;
  }
}

/**
 * Build decorations for all [@citation] patterns in the document
 */
function buildDecorations(doc: any): DecorationSet {
  const decorations: Decoration[] = [];
  const documentCitations = collectDocumentCitations(doc);

  for (const docCitation of documentCitations) {
    const { id, key, from, to, locator } = docCitation;

    // Check if item is registered in citation service
    const isRegistered = citationService.hasItem(key);

    // Check for position-aware cached citation first, then fall back to simple cache
    const positionAwareFormatted = positionAwareCitationCache.get(id);
    const simpleCachedCitation = isRegistered ? citationService.getCachedCitation(key) : null;
    const isResolved = isRegistered && (positionAwareFormatted !== undefined || simpleCachedCitation !== null);

    // Determine citation state and text to show
    let formattedText: string;
    let state: CitationState;
    let cslItem: any = null;

    if (isResolved) {
      state = 'resolved';
      // Prefer position-aware formatting (includes ibid., op. cit., etc.)
      if (positionAwareFormatted !== undefined) {
        formattedText = positionAwareFormatted;
        console.log('[citationPlugin] Using position-aware format for', key, ':', formattedText.substring(0, 50));
      } else if (simpleCachedCitation) {
        formattedText = simpleCachedCitation;
        if (locator) {
          formattedText += `, ${locator}`;
        }
      } else {
        formattedText = `@${key}`;
      }
      // Get the CSL item for field-aware rendering
      cslItem = citationService.getItem(key);
    } else if (isRegistered) {
      // Pending: item registered but not yet formatted by citeproc
      state = 'pending';
      formattedText = `@${key}`;
    } else {
      // Unresolved: item not found in Zotero library
      state = 'unresolved';
      formattedText = `@${key}`;
    }

    // Hide the raw [@key] text
    decorations.push(
      Decoration.inline(from, to, {
        class: "citation-hidden",
      })
    );

    // Insert badge widget
    // Include content hash in key to force widget recreation when citation changes
    let contentHash = formattedText.length + '-' + (formattedText.charCodeAt(0) || 0);
    if (cslItem) {
      const itemStr = JSON.stringify(cslItem);
      let hash = 0;
      for (let i = 0; i < itemStr.length; i++) {
        const char = itemStr.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      contentHash += `-${hash}`;
    }
    // Also include formatted text in hash to detect ibid changes
    contentHash += `-${formattedText.length}`;

    // Create citation object directly from document citation data
    const citation: ParsedCitation = {
      key: docCitation.key,
      locator: docCitation.locator,
      label: docCitation.label,
      raw: docCitation.raw,
    };

    decorations.push(
      Decoration.widget(
        from,
        () => createCitationBadge(citation, formattedText, state, cslItem, from, to),
        {
          side: -1,
          key: `citation-${from}-${key}-${state}-${contentHash}`,
        }
      )
    );
  }

  // Also handle bibliography blocks
  const bibBlocks = findBibliographyBlocks(doc);
  for (const block of bibBlocks) {
    // Hide the :::bibliography marker
    decorations.push(
      Decoration.inline(block.from, block.to, {
        class: "bibliography-marker-hidden",
      })
    );

    // Insert bibliography widget
    decorations.push(
      Decoration.widget(
        block.from,
        () => {
          // Create a stable container that won't be replaced (avoids ProseMirror position errors)
          const container = document.createElement('div');
          container.className = 'bibliography-block-container';

          // Add loading state inside the container
          const loading = document.createElement('div');
          loading.className = 'bibliography-placeholder';
          loading.textContent = 'Chargement de la bibliographie...';
          container.appendChild(loading);

          // Load the bibliography asynchronously and update container contents
          createBibliographyBlockWidget(block.citedOnly).then(widget => {
            // Only update if container is still in DOM
            if (container.isConnected) {
              // Clear container and add the loaded widget content
              container.innerHTML = '';
              // Move all children from widget to container (instead of replacing container)
              while (widget.firstChild) {
                container.appendChild(widget.firstChild);
              }
              // Copy class from widget
              container.className = widget.className;
            }
          });

          return container;
        },
        {
          side: -1,
          key: `bibliography-${block.from}-${block.citedOnly}`,
        }
      )
    );
  }

  console.log('[citationPlugin] Total decorations created:', decorations.length, '(including', bibBlocks.length, 'bibliography blocks)');
  return DecorationSet.create(doc, decorations);
}

// ============================================================================
// PLUGIN
// ============================================================================

/**
 * ProseMirror plugin that renders [@citation] patterns as visual badges
 */
export const citationPlugin = $prose(() => {
  console.log('[citationPlugin] Plugin factory called');
  let editorView: any = null;
  let cslStyleLoaded = false;

  // Subscribe to Zotero items changes to register them with citation service
  const unsubscribeItems = zoteroItems.subscribe((items) => {
    if (items.length > 0) {
      citationService.registerItems(items);

      // Trigger decoration rebuild
      if (editorView) {
        const tr = editorView.state.tr.setMeta(citationPluginKey, { itemsChanged: true });
        editorView.dispatch(tr);
      }
    }
  });

  // Subscribe to templates changes to load CSL style
  const unsubscribeTemplates = templates.subscribe((allTemplates) => {
    console.log('[citationPlugin] Templates updated, count:', allTemplates.length);

    // Debug: log all templates to see their structure
    allTemplates.forEach((t: Template) => {
      if (t.templateType === 'csl' || t.templateSection === 'csl' || t.name?.toLowerCase().includes('csl')) {
        console.log('[citationPlugin] Found potential CSL template:', {
          id: t.id,
          name: t.name,
          templateType: t.templateType,
          templateSection: t.templateSection,
          hasContent: !!t.content,
          contentLength: t.content?.length || 0,
        });
      }
    });

    const cslTemplate = allTemplates.find((t: Template) => {
      const match = t.templateSection === 'csl' || t.templateType === 'csl' || t.name?.toLowerCase().includes('.csl');
      if (match) {
        console.log('[citationPlugin] find() matched template:', t.id, 'section:', t.templateSection, 'type:', t.templateType);
      }
      return match;
    });

    console.log('[citationPlugin] CSL template found:', cslTemplate ? `${cslTemplate.id} (content: ${cslTemplate.content?.length || 0} chars)` : 'none');

    console.log('[citationPlugin] CSL template check:', {
      found: !!cslTemplate,
      hasContent: !!cslTemplate?.content,
      cslStyleLoaded,
      contentLength: cslTemplate?.content?.length || 0
    });

    if (cslTemplate?.content && !cslStyleLoaded) {
      console.log('[citationPlugin] Loading CSL style from template:', cslTemplate.name, 'length:', cslTemplate.content.length);
      citationService.setStyle(cslTemplate.content);
      cslStyleLoaded = true;

      // Trigger decoration rebuild
      if (editorView) {
        const tr = editorView.state.tr.setMeta(citationPluginKey, { styleChanged: true });
        editorView.dispatch(tr);
      }
    }
  });

  return new Plugin({
    key: citationPluginKey,
    state: {
      init(_, { doc }) {
        return buildDecorations(doc);
      },
      apply(tr, oldDecorations) {
        if (tr.docChanged || tr.getMeta(citationPluginKey)) {
          return buildDecorations(tr.doc);
        }
        return oldDecorations.map(tr.mapping, tr.doc);
      },
    },
    view(view) {
      console.log('[citationPlugin] Plugin view mounted');
      editorView = view;
      globalEditorView = view; // Store globally for locator updates

      // Initialize citation service
      citationService.init().catch((error) => {
        console.error('Failed to initialize citation service:', error);
      });

      // Load CSL style if available
      const cslStyle = getCslStyle();
      console.log('[citationPlugin] view() getCslStyle:', {
        found: !!cslStyle,
        length: cslStyle?.length || 0,
        cslStyleLoaded
      });
      if (cslStyle && !cslStyleLoaded) {
        console.log('[citationPlugin] view() loading CSL style, length:', cslStyle.length);
        citationService.setStyle(cslStyle);
        cslStyleLoaded = true;
      }

      // Register current Zotero items
      const currentItems = get(zoteroItems);
      if (currentItems.length > 0) {
        citationService.registerItems(currentItems);
      }

      // Load citation items from document (async - will trigger decoration rebuild when done)
      loadCitationItemsForDocument(view.state.doc).then(() => {
        // After items are loaded, process citations in order for ibid/op. cit. support
        processDocumentCitationsAsync(view.state.doc, editorView);
      }).catch((error) => {
        console.error('[citationPlugin] Failed to load citation items:', error);
      });

      // Trigger bidirectional sync on document open
      (async () => {
        try {
          const { bibliographyService } = await import('../services/bibliographyService');
          const { selectedProject } = await import('../stores/appStore');
          const project = get(selectedProject);
          if (project) {
            console.log('[citationPlugin] Running bidirectional sync on document open');
            const syncResult = await bibliographyService.bidirectionalSync(project.id);
            console.log('[citationPlugin] Bidirectional sync result:', syncResult);

            // If items were pulled from Zotero, refresh the citation service
            if (syncResult.pulled > 0) {
              const bibliography = await bibliographyService.getBibliography(project.id);
              if (bibliography) {
                // Items are in Zotero format - convert to CSL on-the-fly
                await citationService.registerZoteroDataItems(Object.values(bibliography.items));
                // Process citations in order for ibid/op. cit. support
                if (editorView) {
                  await processDocumentCitationsAsync(editorView.state.doc, editorView);
                }
              }
            }

            // Start periodic bidirectional sync (every 60 seconds)
            bibliographyService.startPeriodicSync(project.id, async (result) => {
              console.log('[citationPlugin] Periodic sync detected changes:', result);
              // Also run bidirectional sync after periodic sync
              const biSyncResult = await bibliographyService.bidirectionalSync(project.id);
              if (biSyncResult.pulled > 0 && editorView) {
                const bibliography = await bibliographyService.getBibliography(project.id);
                if (bibliography) {
                  // Items are in Zotero format - convert to CSL on-the-fly
                  await citationService.registerZoteroDataItems(Object.values(bibliography.items));
                  // Process citations in order for ibid/op. cit. support
                  await processDocumentCitationsAsync(editorView.state.doc, editorView);
                }
              }
            });
          }
        } catch (error) {
          console.warn('[citationPlugin] Bidirectional sync error:', error);
        }
      })();

      // Subscribe to citation cache updates to refresh decorations
      const unsubscribeCacheUpdate = citationService.onCacheUpdate(() => {
        console.log('[citationPlugin] Citation cache updated, processing for position awareness');
        if (editorView) {
          // Process citations in order for ibid/op. cit. support
          processDocumentCitationsAsync(editorView.state.doc, editorView);
        }
      });

      return {
        destroy() {
          editorView = null;
          globalEditorView = null;
          unsubscribeItems();
          unsubscribeTemplates();
          unsubscribeCacheUpdate();
          // Stop periodic sync when editor is unmounted
          import('../services/bibliographyService').then(({ bibliographyService }) => {
            bibliographyService.stopPeriodicSync();
          });
        },
      };
    },
    props: {
      decorations(state) {
        return citationPluginKey.getState(state) as DecorationSet;
      },
    },
  });
});

// ============================================================================
// CITATION INSERTION HELPERS
// ============================================================================

/**
 * Create a citation markdown string
 */
export function createCitationMarkdown(
  key: string,
  locator?: string,
  label?: string
): string {
  if (locator) {
    const labelPrefix = label === 'page' ? 'p. ' :
                        label === 'chapter' ? 'ch. ' :
                        label === 'section' ? 'sec. ' :
                        '';
    return `[@${key}, ${labelPrefix}${locator}]`;
  }
  return `[@${key}]`;
}

/**
 * Insert a citation at the current cursor position
 */
export function insertCitation(
  view: any,
  key: string,
  locator?: string,
  label?: string
): void {
  const citationText = createCitationMarkdown(key, locator, label);
  const { state, dispatch } = view;
  const { from, to } = state.selection;

  const tr = state.tr.insertText(citationText, from, to);
  dispatch(tr);
}

export default citationPlugin;
